import {
  CONSENT_MSG,
  CONSENT_REQUEST_MSG,
  type ConsentState,
  listenForChildRequests,
} from './consentChildListener';
import { initGtag, isLocalHost, setGtagConsent } from './gtag';

type BootstrapConfig = {
  gtagId: string;
  firePageView?: boolean;
};

type CookieScriptGlobal = {
  instance?: { currentState(): ConsentState };
};

type ReactNativeWebView = {
  postMessage(msg: string): void;
};

declare global {
  interface Window {
    __racemapConsent?: ConsentState | null;
    __racemapConsentConfig?: BootstrapConfig;
    __racemapConsentBootstrapInitialized?: boolean;
    CookieScript?: CookieScriptGlobal;
    ReactNativeWebView?: ReactNativeWebView;
  }
}

const COOKIE_SCRIPT_SRC = '//cdn.cookie-script.com/s/6daad9cd901f9a7ad0c15e1a6c64e84f.js';
const COOKIE_SCRIPT_EVENTS: ReadonlyArray<string> = [
  'CookieScriptLoaded',
  'CookieScriptAcceptAll',
  'CookieScriptAccept',
  'CookieScriptReject',
];

function getParentOrigin(): string | null {
  if (window.self === window.top) return null;
  try {
    return new URL(document.referrer).origin;
  } catch {
    return null;
  }
}

function isReactNativeWebView(): boolean {
  return typeof window.ReactNativeWebView?.postMessage === 'function';
}

function hasPerformanceConsent(consent: ConsentState | null): boolean {
  return consent?.categories?.includes('performance') === true;
}

function broadcastConsentToChildren(consent: ConsentState): void {
  for (const frame of document.querySelectorAll<HTMLIFrameElement>('iframe')) {
    if (!frame.src) continue;
    try {
      const origin = new URL(frame.src, window.location.href).origin;
      frame.contentWindow?.postMessage({ type: CONSENT_MSG, consent }, origin);
    } catch {
      // ignore malformed iframe src
    }
  }
}

function applyConsent(consent: ConsentState | null): void {
  window.__racemapConsent = consent;
  setGtagConsent(hasPerformanceConsent(consent));
  window.dispatchEvent(new CustomEvent('racemap:consent', { detail: consent }));
  if (consent != null) broadcastConsentToChildren(consent);
}

function requestConsentFromParent(parentOrigin: string): void {
  window.addEventListener('message', (e) => {
    if (e.origin !== parentOrigin) return;
    if (e.source !== window.parent) return;
    const data = e.data as { type?: string; consent?: ConsentState } | null;
    if (data?.type === CONSENT_MSG && data.consent !== undefined) {
      applyConsent(data.consent);
    }
  });
  try {
    window.parent.postMessage({ type: CONSENT_REQUEST_MSG }, parentOrigin);
  } catch {
    // cross-origin parent may throw on postMessage — ignore
  }
}

type ReactNativeDataInboundData = { type?: string; consent?: ConsentState };

/**
 * React Native WebView bridge:
 * - Outbound: window.ReactNativeWebView.postMessage(string) → triggers onMessage in RN
 * - Inbound:  the RN app calls webViewRef.injectJavaScript(...) or uses postMessage via
 *             the injected bridge; messages arrive as window 'message' events with
 *             e.origin === '' or 'null'.
 *
 * The RN app must respond to the consent-request message by posting back:
 *   { type: 'racemap-cookie-consent', consent: { action: 'accept', categories: [...] } }
 */
function requestConsentFromReactNative(): void {
  window.addEventListener('message', (e) => {
    // RN-injected messages have an empty or 'null' origin
    if (e.origin !== '' && e.origin !== 'null') return;
    let data: ReactNativeDataInboundData | null = null;
    try {
      data =
        typeof e.data === 'string'
          ? (JSON.parse(e.data) as ReactNativeDataInboundData)
          : (e.data as ReactNativeDataInboundData);
    } catch {
      return;
    }
    if (data?.type === CONSENT_MSG && data.consent !== undefined) {
      applyConsent(data.consent);
    }
  });
  try {
    window.ReactNativeWebView?.postMessage(JSON.stringify({ type: CONSENT_REQUEST_MSG }));
  } catch {
    // ignore
  }
}

function loadCookieScript(): void {
  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.charset = 'UTF-8';
  script.src = COOKIE_SCRIPT_SRC;
  document.head.appendChild(script);

  for (const ev of COOKIE_SCRIPT_EVENTS) {
    document.addEventListener(ev, () => {
      try {
        applyConsent(window.CookieScript?.instance?.currentState() ?? null);
      } catch {
        // CookieScript API not ready — ignore
      }
    });
  }
}

function bootstrapConsent(config: BootstrapConfig): void {
  if (isLocalHost()) return;
  if (window.__racemapConsentBootstrapInitialized === true) return;
  window.__racemapConsentBootstrapInitialized = true;

  initGtag(config.gtagId);
  if (config.firePageView === true) {
    window.gtag?.('event', 'page_view', { page_title: document.title });
  }
  window.__racemapConsent = null;

  listenForChildRequests(() => window.__racemapConsent ?? null);

  const parentOrigin = getParentOrigin();
  if (parentOrigin !== null) {
    requestConsentFromParent(parentOrigin);
    console.info(
      `Consent bootstrap: detected embedded environment with parent origin ${parentOrigin}, requesting consent from parent frame`,
    );
  } else if (isReactNativeWebView()) {
    requestConsentFromReactNative();
    console.info(
      'Consent bootstrap: detected React Native WebView environment, using postMessage bridge for consent',
    );
  } else {
    loadCookieScript();
  }
}

const config = window.__racemapConsentConfig;
if (config === undefined) {
  console.error(
    'consentBootstrap: window.__racemapConsentConfig not set before loading this script.',
  );
} else {
  bootstrapConsent(config);
}
